import { Col, Container, Row, Spinner, Form, Button, Stack } from "react-bootstrap";
import { useParams } from 'react-router-dom';
import { useEffect, useRef, useState } from "react";
import AlertMessage from "../../../components/AlertMessage.tsx";
import { useTranslation } from "react-i18next";
import { ClaimFileDto, ClaimFollowUpRequestDto, ClaimFollowUpResponseDto } from "../types/index.ts";
import { ApiErrorResponse } from "../../../types/ApiErrorResponse.ts";
import { useAlert } from "../../../hooks/useAlert.ts";
import { getClaimFollowUpRequest, getDownloadFile, sendClaimFollowUpResponse } from "../api/claimFollowUpAPI.ts";
import ClaimAttachDocumentsModal from "../modals/ClaimAttachDocumentsModal.tsx";
import SelectedFilterChip from "../../../components/SelectedFilterChip.tsx";
import { FaDownload } from "react-icons/fa";

export const ClaimFollowUpResponse = () => {
    const { t, i18n } = useTranslation(['claimFollowUp', 'common']);
    const { id } = useParams();
    const [loading, setLoading] = useState(false);
    const [showAttachDocumentsModal, setShowAttachDocumentsModal] = useState(false);
    
    const { showAlert, hideAlert, alert, isVisible } = useAlert({ fromLocation: false });

    const [sendMessage, setSendMessage] = useState(false);
    const [claimFollowUpRequestDto, setClaimFollowUpRequestDto] = useState<ClaimFollowUpRequestDto>();

    const [formData, setFormData] = useState<ClaimFollowUpResponseDto>({ response: ""});

    useEffect(() => {
        if (!id) return;
        setLoading(true);
        getClaimFollowUpRequest(id)
            .then(data => {
                setClaimFollowUpRequestDto(data);
                if (data?.responseTemplate) {
                    setFormData(prev => ({ ...prev, response: data.responseTemplate! }));
                }

                let languageCode = "";                
                switch(data.clientLanguage) {
                    case 'Catalan':
                        languageCode = 'ca';
                        break;
                    case 'Spanish':
                        languageCode = 'es';
                        break;
                    case 'English':
                        languageCode = 'en';
                        break;
                    case 'French':
                        languageCode = 'fr';
                        break;
                }

                if (languageCode) {
                    i18n.changeLanguage(languageCode);
                }
            })
            .catch((err: ApiErrorResponse) => {
                console.error(err);
            })
            .finally(() => setLoading(false));
    }, [id]);

    const handleUpdateResponse = async () => {
        const newErrors: { response?: string } = {};
        if (!formData.response.trim()) {
            newErrors.response = t("common:form.requiredFieldForm");
        }
        try {
            setLoading(true);
            await sendClaimFollowUpResponse(id!, formData);
            setLoading(false);
            setSendMessage(true);
            showAlert({
                message: t("claimFollowUp:messageSendMessageSuccessfully"),
                type: "success",
                duration: 5000,
            });

        } catch (e: any) {
            setLoading(false);
            const errorMessage = e?.code === "ERROR_DOCUMENT_ALREADY_EXISTS"
                ? t(`claimFollowUp:${e.code}`, { name: e.name })
                : e?.message || t("common:genericFormErrors.unexpectedErorr");
            
            showAlert({
                message: errorMessage,
                type: "danger",
                duration: 5000,
            });
        }
    };

    const handleCloseWindow = () => {
        window.close();
    };
        
    const handleRemoveFile = (indexToRemove: number) => {
        const nextFiles = (formData.files || []).filter((_, index) => index !== indexToRemove);
        setFormData((prev) => ({
            ...prev,
            files: nextFiles.length > 0 ? nextFiles : undefined
        }));
    };

    const handleRemoveClaimFile = (indexToRemove: number) => {
        const nextFiles = (formData.claimFiles || []).filter((_, index) => index !== indexToRemove);
        setFormData((prev) => ({
            ...prev,
            claimFiles: nextFiles.length > 0 ? nextFiles : undefined,
        }));
    };
    
    const addAttachedDocuments = (data: { selectedClaimFiles?: ClaimFileDto[]; addedFiles?: File[] }) => {
        setFormData((prev) => {
            return {
                ...prev,
                files: [...(data.addedFiles || [])],
                claimFiles: [...(data.selectedClaimFiles || [])]
            };
        });
    }

    const fileInputRef = useRef<HTMLInputElement | null>(null);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || []);
        if (files.length === 0) return;

        setFormData((prev) => {
            const existing = Array.isArray(prev.files)
                ? prev.files
                : prev.files
                    ? [prev.files]
                    : [];
            return {
                ...prev,
                files: [...existing, ...files]
            };
        });

        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const downloadFile = async (publicId: string) => {
        const file = await getDownloadFile(publicId);

        const byteCharacters = atob(file.data);
        const byteNumbers = new Array(byteCharacters.length);

        for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
        }

        const blob = new Blob(
            [new Uint8Array(byteNumbers)],
            { type: file.contentType }
        );

        const url = URL.createObjectURL(blob);

        const newWindow = window.open("", "_blank");

        if (!newWindow) return;

        newWindow.document.title = file.fileName;

        if (file.contentType === "application/pdf") {
            newWindow.document.write(`
            <html>
                <head>
                    <title>${file.fileName}</title>
                </head>
                <body style="margin:0">
                    <iframe 
                        src="${url}" 
                        style="width:100%;height:100vh;border:none">
                    </iframe>
                </body>
            </html>
        `);
        } else {
            newWindow.location.href = url;
        }

        newWindow.document.close();
        setTimeout(() => {
            URL.revokeObjectURL(url);
        }, 60000);
    };
    
    return (
        <Container fluid className="vh-90 pl-5">
            <ClaimAttachDocumentsModal
                claimId={claimFollowUpRequestDto?.claimId ?? 0}
                show={showAttachDocumentsModal}
                possibleClaimFiles={claimFollowUpRequestDto?.claimFiles ?? []}
                onClose={() => setShowAttachDocumentsModal(false)}
                onSubmit={addAttachedDocuments}
            />
            {loading && (
                <div className="overlay-spinner">
                    <Spinner animation="border" variant="light" />
                </div>
            )}
            <Row className="h-90 justify-content-center">
                <Col md={9} className="d-flex flex-column justify-content-center align-items-center">
                    <div className="w-100" style={{ maxWidth: '320px' }}>
                        <img
                            src={claimFollowUpRequestDto?.clientLogo
                                ? `data:${claimFollowUpRequestDto.clientLogoContentType};base64,${claimFollowUpRequestDto.clientLogo}`
                                : "/logo-craiteria.svg"
                            }
                            alt="Craiteria"
                            style={{ width: '100%' }}
                        />
                    </div>

                    <h4 className="text-center">
                        {t("claimFollowUp:followUpResonse.title")}
                    </h4>
                </Col>
            </Row>
            <Row>
                <Col md={12}>
                    {!sendMessage 
                        ? t("claimFollowUp:followUpResonse.subtitle", { id: claimFollowUpRequestDto?.claimId ?? "" })
                        : t("claimFollowUp:followUpResonse.sendMessageSubtitle")
                    }
                </Col>
            </Row>
            <Row className="mb-3">
                <Col md={12}>
                    {isVisible && (
                        <AlertMessage
                            message={ alert!.message }
                            type = { alert!.type }
                            onClose = { hideAlert }/>
                    )}
                </Col>
            </Row>
            {!sendMessage && (
                <>
                    <Row>
                        <Col md={12}>
                            <Form.Group>
                                <Form.Control
                                    as="textarea"
                                    rows={10}
                                    value={claimFollowUpRequestDto?.request ?? ""}
                                    disabled={true}
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row className="justify-content-start align-items-center mb-2">
                        <Col xs="auto" className="text-start">
                            <strong>{t("claimFollowUp:followUpResonse.documents")}:</strong>{" "}
                            {claimFollowUpRequestDto?.claimFiles?.map((file) => (
                                <span key={file.publicId} className="ms-3" >
                                    <span
                                        onClick={() => downloadFile(file.publicId)}
                                        style={{ cursor: "pointer", color: "#5DB77A" }}
                                    >
                                        <FaDownload className="me-1" />
                                        {file.fileName}
                                    </span>
                                </span>
                            ))}
                        </Col>
                    </Row>
                    <Row>
                        <Col md={12}>
                            <Form.Group>
                                <Form.Control
                                    as="textarea"
                                    rows={10}
                                    value={formData.response}
                                    onChange = {(e) => setFormData({
                                        ...formData,
                                        response: e.target.value,
                                    })}
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row>
                        <Col xs={12} md={12}>
                            <Form.Control
                                className="d-none"
                                type="file"
                                ref={fileInputRef}
                                onChange={handleFileChange}
                                multiple
                            />
                            <Row>
                                <Col md={10}>
                                    {((formData.files || []).length > 0 || (formData.claimFiles || []).length > 0) &&
                                        <Stack direction="horizontal" gap={2} className="d-flex align-items-center gap-2 flex-wrap">
                                            <span><strong>{t("claimFollowUp:followUpComment.fileListAttachment")}</strong></span>
                                            {(formData.files || []).map((file, index) => {
                                                return (
                                                    <SelectedFilterChip
                                                        key={`${file.name}-${index}`}
                                                        label=""
                                                        value={file.name}
                                                        onRemove={() =>
                                                            handleRemoveFile(index)
                                                        }
                                                    />
                                                );
                                            })}
                                            {(formData.claimFiles || []).map((file, index) => {
                                                return (
                                                    <SelectedFilterChip
                                                        key={`${file.fileName}-${index}`}
                                                        label=""
                                                        value={file.fileName}
                                                        onRemove={() =>
                                                            handleRemoveClaimFile(index)
                                                        }
                                                    />
                                                );
                                            })}
                                        </Stack>
                                    }
                                </Col>
                                <Col md={2} className="d-flex justify-content-end align-items-start">
                                    <a href="#" className="ms-auto" onClick={() => setShowAttachDocumentsModal(true)}>
                                        <p className="text-secondary text-nowrap mb-0">
                                            {t("claimFollowUp:followUpReply.buttonAttachment")}
                                        </p>
                                    </a>
                                </Col>
                            </Row>
                        </Col>
                    </Row>
                    <Row className="mb-3 mt-3">
                        <Col className="d-flex justify-content-end">
                            <Button
                                variant="primary"
                                onClick={() => handleUpdateResponse()}>{t("claimFollowUp:followUpResonse.buttonSaveResponse")}
                            </Button>
                        </Col>
                    </Row>
                </> 
            )}
            {sendMessage && (
                <Row className="mb-3">
                    <Col className="d-flex justify-content-center">
                        <Button
                            variant="primary"
                            onClick={handleCloseWindow}
                        >
                            {t("claimFollowUp:followUpResonse.buttonCloseWindow")}                            
                        </Button>
                    </Col>
                </Row>
            )}
        </Container>
    );
}